import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, CheckCircle, FileText, Mail, Send, AlertCircle, ChevronDown, ChevronUp, Phone, User } from 'lucide-react';
import API_BASE_URL from '../config/api';
import { useClinic } from '../contexts/ClinicContext';

const STAGES = [
  { key: 'formPending', label: 'Form Pending', color: '#dc2626', icon: Mail, desc: 'Form email sent, awaiting patient response' },
  { key: 'awaitingLetters', label: 'Awaiting Letters', color: '#ea580c', icon: FileText, desc: 'Form completed, letters not yet generated' },
  { key: 'awaitingSlots', label: 'Awaiting Slots', color: '#d97706', icon: Clock, desc: 'Letters generated, appointment slots not yet sent' },
  { key: 'awaitingPatientResponse', label: 'Awaiting Booking', color: '#2563eb', icon: Send, desc: 'Slots sent, patient hasn\'t chosen yet' },
  { key: 'awaitingGensolve', label: 'Confirm in Gensolve', color: '#16a34a', icon: CheckCircle, desc: 'Patient confirmed, book in Gensolve' },
  { key: 'telehealthDeclined', label: 'Telehealth Declined', color: '#7c3aed', icon: Phone, desc: 'Patient needs in-person appointment' },
];

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function ReferralPipeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStage, setExpandedStage] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const { activeClinicId } = useClinic();
  const navigate = useNavigate();

  const fetchPipeline = async () => {
    try {
      const clinicQuery = activeClinicId ? `?clinicId=${activeClinicId}` : '';
      const res = await axios.get(`${API_BASE_URL}/referrals/pipeline${clinicQuery}`);
      setData(res.data);
    } catch (err) {
      console.error('Pipeline fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPipeline(); }, [activeClinicId]);

  const handleAction = async (patientId, action, extra) => {
    if (action === 'resendForm') {
      const email = window.prompt(
        `Send the patient form email to ${extra?.name || 'this patient'}.\n\nConfirm or update the email address:`,
        extra?.email || ''
      );
      if (email === null) return;
      if (!email.trim()) { alert('Email address is required.'); return; }
      extra = { ...extra, confirmedEmail: email.trim() };
    }

    setActionLoading(prev => ({ ...prev, [patientId]: true }));
    try {
      switch (action) {
        case 'resendForm':
          await axios.post(`${API_BASE_URL}/patients/${patientId}/send-form-email`, { email: extra.confirmedEmail });
          break;
        case 'dismiss':
          if (!window.confirm('Remove this patient from the pipeline? (marks as booked)')) return;
          await axios.post(`${API_BASE_URL}/referrals/pipeline/dismiss/${patientId}`);
          break;
        case 'viewFile':
          navigate(`/patient-file/${patientId}`);
          return;
        default:
          break;
      }
      await fetchPipeline();
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed');
    } finally {
      setActionLoading(prev => ({ ...prev, [patientId]: false }));
    }
  };

  if (loading) {
    return <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>Loading pipeline...</div>;
  }

  if (!data) return null;

  const { pipeline, summary } = data;

  return (
    <div style={{ padding: '24px' }}>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {STAGES.map(stage => {
          const count = summary[stage.key] || 0;
          const overdueCount = (pipeline[stage.key] || []).filter(p => p.isOverdue).length;
          const Icon = stage.icon;
          return (
            <div
              key={stage.key}
              onClick={() => count > 0 && setExpandedStage(expandedStage === stage.key ? null : stage.key)}
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '16px',
                borderLeft: `4px solid ${stage.color}`,
                cursor: count > 0 ? 'pointer' : 'default',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                opacity: count === 0 ? 0.5 : 1,
                transition: 'box-shadow 0.2s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Icon size={16} color={stage.color} />
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>{stage.label}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                <span style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>{count}</span>
                {overdueCount > 0 && (
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#dc2626', backgroundColor: '#fef2f2', padding: '2px 6px', borderRadius: '4px' }}>
                    {overdueCount} overdue
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Total bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '8px 16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
        <span style={{ fontSize: '14px', color: '#6b7280' }}>
          <strong>{summary.total}</strong> active referral{summary.total !== 1 ? 's' : ''} in pipeline
        </span>
        {summary.overdue > 0 && (
          <span style={{ fontSize: '13px', color: '#dc2626', fontWeight: '600' }}>
            <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
            {summary.overdue} need attention
          </span>
        )}
      </div>

      {/* Expanded Stage Detail */}
      {STAGES.map(stage => {
        const patients = pipeline[stage.key] || [];
        const isExpanded = expandedStage === stage.key;
        if (patients.length === 0) return null;

        return (
          <div key={stage.key} style={{ marginBottom: '12px', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div
              onClick={() => setExpandedStage(isExpanded ? null : stage.key)}
              style={{
                padding: '12px 16px',
                backgroundColor: isExpanded ? stage.color : '#f9fafb',
                color: isExpanded ? 'white' : '#374151',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'background-color 0.2s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <stage.icon size={18} />
                <span style={{ fontWeight: '600', fontSize: '15px' }}>{stage.label}</span>
                <span style={{
                  backgroundColor: isExpanded ? 'rgba(255,255,255,0.25)' : stage.color,
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {patients.length}
                </span>
              </div>
              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>

            {isExpanded && (
              <div style={{ padding: '0' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', padding: '8px 16px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {stage.desc}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      <th style={thStyle}>Patient</th>
                      <th style={thStyle}>Injury</th>
                      <th style={thStyle}>Referrer</th>
                      <th style={thStyle}>Waiting</th>
                      <th style={thStyle}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patients.map(p => (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: '500', color: '#111827' }}>{p.name}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {p.dateOfBirth || ''}{p.nhi ? ` | ${p.nhi}` : ''}{p.accNumber ? ` | ACC: ${p.accNumber}` : ''}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {p.email}{p.phone ? ` | ${p.phone}` : ''}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: '13px', color: '#374151' }}>{p.injuryType || '-'}</span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: '13px', color: '#374151' }}>{p.referrerName || '-'}</span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: p.isOverdue ? '600' : '400',
                            color: p.isOverdue ? '#dc2626' : '#6b7280'
                          }}>
                            {timeAgo(p.stageDate)}
                            {p.isOverdue && ' !'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {stage.key === 'formPending' && (
                              <button
                                onClick={() => handleAction(p.id, 'resendForm', { name: p.name, email: p.email })}
                                disabled={actionLoading[p.id]}
                                style={btnStyle('#e8941c')}
                              >
                                {actionLoading[p.id] ? '...' : 'Resend Form'}
                              </button>
                            )}
                            <button
                              onClick={() => handleAction(p.id, 'viewFile')}
                              style={btnStyle('#2563eb')}
                            >
                              View File
                            </button>
                            <button
                              onClick={() => handleAction(p.id, 'dismiss')}
                              disabled={actionLoading[p.id]}
                              style={btnStyle('#6b7280')}
                            >
                              Dismiss
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const thStyle = { padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' };
const tdStyle = { padding: '12px 16px', verticalAlign: 'top' };
const btnStyle = (bg) => ({
  padding: '4px 12px',
  backgroundColor: bg,
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: '500',
  whiteSpace: 'nowrap',
});

export default ReferralPipeline;
